import { connect } from 'react-redux'

/**
 * Internal utility function to safely handle potentially circular references
 * This is a standalone implementation similar to sanitizeForRedux
 */
export const safeSerialize = (data) => {
  if (data === undefined) {
    return null
  }
  if (data === null || typeof data !== 'object') {
    return data
  }
  try {
    return JSON.parse(JSON.stringify(data))
  } catch (e) {
    // Fallback approach for objects with circular references
    if (Array.isArray(data)) {
      return data.map(item => safeSerialize(item))
    }
    
    if (typeof data === 'object') {
      const result = {}
      Object.keys(data).forEach(key => {
        try {
          result[key] = safeSerialize(data[key])
        } catch (innerError) {
          result[key] = null // Set to null if we can't serialize
        }
      })
      return result
    }
    
    return data // Return original as last resort
  }
}

/**
 * Recursively converts immutable objects to plain JavaScript objects
 * Works with any object that has an .asMutable() method
 */
export const toMutable = (value) => {
  if (!value) return value
  
  // Handle immutable objects with asMutable method
  if (typeof value === 'object' && value !== null && typeof value.asMutable === 'function') {
    try {
      return value.asMutable({ deep: true })
    } catch (e) {
      // Continue with other approaches if asMutable fails
    }
  }
  
  // Handle arrays - map each item through toMutable
  if (Array.isArray(value)) {
    return value.map(item => toMutable(item))
  }
  
  // Handle plain objects - recursively process each property
  if (typeof value === 'object' && value !== null) {
    const result = {}
    Object.keys(value).forEach(key => {
      result[key] = toMutable(value[key])
    })
    return result
  }
  
  // Primitive values pass through unchanged
  return value
}

/**
 * Combined approach to normalize state objects for React components
 */
export const normalizePropData = (value) => {
  try {
    // First, try converting immutable data
    const mutableData = toMutable(value)
    
    // Then, handle any circular references
    return safeSerialize(mutableData)
  } catch (e) {
    // Last resort fallback
    try {
      // Try JSON serialization as a last resort
      return JSON.parse(JSON.stringify(value))
    } catch (jsonError) {
      return value // Return original as absolute last resort
    }
  }
}

/**
 * Wraps mapStateToProps function to automatically convert immutable objects to plain JS
 */
const wrapMapStateToProps = (mapStateToProps) => {
  if (!mapStateToProps) return mapStateToProps
  
  return (state, ownProps) => {
    try {
      // Get props from original mapStateToProps
      const props = mapStateToProps(state, ownProps)
      
      // Normalize all props
      if (typeof props === 'object' && props !== null) {
        const normalizedProps = {}
        Object.keys(props).forEach(key => {
          normalizedProps[key] = normalizePropData(props[key])
        })
        return normalizedProps
      }
      
      return props
    } catch (e) {
      return {} // Return empty object as fallback to prevent crashes
    }
  }
}

/**
 * Drop-in replacement for Redux connect that automatically converts immutable props to plain JS
 */
export const MutableConnect = (mapStateToProps, mapDispatchToProps, mergeProps, options) => connect(
    wrapMapStateToProps(mapStateToProps),
    mapDispatchToProps,
    mergeProps,
    options,
  )

export default MutableConnect
